import React, { useEffect, useMemo } from 'react';

import { lookupNumber } from 'flow/actions/contact-actions';
import { routeUserToOmnichannelErrand } from 'flow/actions/omnichannel-actions';
import { useHistory } from 'react-router-dom';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import { sendPickupQueue } from 'flow/actions/top-actions';
import MenuItem from 'flow/components/ui-components/MenuItem';
import useAppContext from 'flow/hooks/useAppContext';
import useContact from 'flow/hooks/useContact';
import useExtension from 'flow/hooks/useExtension';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { hasActiveCallsBlockingNewCall } from 'flow/selectors/call-selectors';
import { makeSelectContactDataForNumber } from 'flow/selectors/contact-selectors';
import { loggedInUserHasOmnichannelFeature } from 'flow/selectors/extension-selectors';
import { selectWaitingQueueItem } from 'flow/selectors/queue-selectors';
import { QueueCallQueueKey } from 'flow/types/EntityKeys';
import { isRegisteredSIP, NewCallBlockReason } from 'flow/utils/call-utils';
import { getCountryCode } from 'flow/utils/extension-utils';
import { getQueueCallDisplayText } from 'flow/utils/pbx-utils';

interface Props {
    callKey: QueueCallQueueKey;
    queuePosition: number;
}

const PbxQueueMenuItem = ({ callKey, queuePosition }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { ownContact } = useContact();
    const { ownExtension } = useExtension();
    const ownCountryCode = getCountryCode(ownContact, null);

    const queueItem = useAppSelector((state) =>
        selectWaitingQueueItem(state, callKey),
    );

    const hasRegisteredSIP = useAppSelector(
        (state) =>
            isRegisteredSIP(ownExtension) || state.phone.get('isRegistered'),
    );

    const callerNumber = queueItem.getIn(['caller', 'number']);
    const isOmnichannelErrand = queueItem.get('isOmnichannelErrand');

    const selectContactData = useMemo(makeSelectContactDataForNumber, []);
    const contactData = useAppSelector((state) =>
        selectContactData(state, callerNumber),
    );

    const userHasOmnichannelFeature = useAppSelector(
        loggedInUserHasOmnichannelFeature,
    );

    const hasActiveCallBlockingNewCall = useAppSelector(
        (state) =>
            hasActiveCallsBlockingNewCall(state, 'incoming', 2) !==
            NewCallBlockReason.NONE,
    );

    useEffect(() => {
        if (!contactData && !isOmnichannelErrand) {
            dispatch(lookupNumber(callerNumber));
        }
    }, [callerNumber, contactData, isOmnichannelErrand]);

    const onClick = () => {
        if (queueItem.get('isTelavoxTicketNonCall')) {
            dispatch(sendPickupQueue(callKey, 'context menu'));
            history.push(
                generatePath(routes['tickets.ticketKey'], {
                    ticketKey: `ticket-${queueItem.get('telavoxOmniTicketId')}`,
                }),
            );
        } else if (queueItem.get('omnichannelErrandDeepLinkId')) {
            dispatch(sendPickupQueue(callKey, 'context menu'));
            dispatch(
                routeUserToOmnichannelErrand(
                    queueItem.get('omnichannelErrandDeepLinkId'),
                    'context menu',
                ),
            );
        } else {
            dispatch(sendPickupQueue(callKey, 'context menu'));
        }
    };

    const disabled =
        hasActiveCallBlockingNewCall ||
        (isOmnichannelErrand ? !userHasOmnichannelFeature : !hasRegisteredSIP);

    const displayText = isOmnichannelErrand
        ? queueItem.get('omnichannelErrandSender')
        : getQueueCallDisplayText(
              callerNumber,
              contactData,
              ownCountryCode,
              i18next,
          );

    return (
        <MenuItem
            className="flex gap-3 px-3"
            onClick={onClick}
            disabled={disabled}
        >
            <span className="text-xs font-bold text-gray-700">
                {queuePosition}
            </span>
            <span className="text-xs text-gray-800">{displayText}</span>
        </MenuItem>
    );
};

export default PbxQueueMenuItem;
